import React, { useState, useEffect, useContext } from 'react';
import SEOHead from './SEOHead';
import { AuthContext } from '../App';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import {
  ChevronLeft, ChevronRight, Star, Check, X, Eye, BookOpen,
  Filter, Search, RefreshCw, Menu, TrendingUp, Target, Lock, Brain, Sparkles
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import NerdAIChatPopup from './NerdAIChatPopup';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const ExamBankPractice = () => {
  const { examId } = useParams();
  const { user, openAuth } = useContext(AuthContext);
  const navigate = useNavigate();

  // State
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [filters, setFilters] = useState({});
  const [needsSubscription, setNeedsSubscription] = useState(false);
  const [isTrialMode, setIsTrialMode] = useState(false);
  const [hasFullAccess, setHasFullAccess] = useState(false);
  const [showSubscriptionModal, setShowSubscriptionModal] = useState(false);
  const [loadingAskNerd, setLoadingAskNerd] = useState(false);
  
  // Determine back navigation based on exam type
  const getBackPath = () => {
    // Check if we're in trial mode
    const urlParams = new URLSearchParams(window.location.search);
    const isTrial = urlParams.get('trial') === 'true';
    
    if (isTrial) {
      return '/free-trial-exams'; // Trial exams go back to trial hub
    }
    
    // Regular navigation based on exam category
    const globalExams = ['gre', 'gmat'];
    const pharmacyExams = ['naplex', 'mpje', 'pebc', 'gphc', 'opra', 'cap', 'dha-pharma', 'sple'];
    const dentistExams = ['dha-dental', 'adc', 'ore', 'inbde'];
    const engineeringExams = ['iit-jee', 'neet-ug'];
    
    if (globalExams.some(exam => examId.includes(exam))) {
      return '/global-exam-bank';
    } else if (pharmacyExams.some(exam => examId.includes(exam))) {
      return '/pharmacy-exam-bank';
    } else if (dentistExams.some(exam => examId.includes(exam))) {
      return '/dentist-exam-bank';
    } else if (engineeringExams.some(exam => examId.includes(exam))) {
      return '/engineering-exam-bank';
    }
    return '/exam-bank'; // Default to doctors
  };
  
  // Filters
  const [selectedSubject, setSelectedSubject] = useState('');
  const [selectedTopic, setSelectedTopic] = useState('');
  const [selectedDifficulty, setSelectedDifficulty] = useState('');
  const [selectedSection, setSelectedSection] = useState(''); // For GRE/GMAT
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Available filter options
  const [subjects, setSubjects] = useState([]);
  const [topics, setTopics] = useState([]);
  const [sections, setSections] = useState([]); // For GRE/GMAT

  // User interactions (stored in localStorage)
  const [userAnswers, setUserAnswers] = useState({});
  const [bookmarks, setBookmarks] = useState(new Set());
  const [revealed, setRevealed] = useState(new Set());
  
  // Nerd-AI Chat state
  const [showAIChat, setShowAIChat] = useState(false);
  const [aiChatQuery, setAIChatQuery] = useState('');
  const [aiChatKey, setAIChatKey] = useState(0); // Key to force re-render
  const [examName, setExamName] = useState('');
  
  // Check if this is a global exam (GRE/GMAT) - they are now PAID, not free
  const isGlobalExam = examId === 'gre' || examId === 'gmat';
  
  // Check if user has AI Buddy access for "Ask Nerd-AI" feature
  const hasAIBuddyAccess = user && (
    user.email === "shifracs@gmail.com" || // Admin
    (user.ai_buddy_expiry && new Date(user.ai_buddy_expiry) > new Date()) // Active AI Buddy subscription
  );

  useEffect(() => {
    fetchQuestions();
  }, [examId, page, selectedSubject, selectedTopic, selectedDifficulty, selectedSection, searchQuery]);

  useEffect(() => {
    // Load from localStorage
    const saved = localStorage.getItem(`exam_${examId}_answers`);
    if (saved) setUserAnswers(JSON.parse(saved));

    const savedBookmarks = localStorage.getItem(`exam_${examId}_bookmarks`);
    if (savedBookmarks) setBookmarks(new Set(JSON.parse(savedBookmarks)));

    const savedRevealed = localStorage.getItem(`exam_${examId}_revealed`);
    if (savedRevealed) setRevealed(new Set(JSON.parse(savedRevealed)));
  }, [examId]);

  const fetchQuestions = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      
      // Check if we're in trial mode from URL
      const urlParams = new URLSearchParams(window.location.search);
      const isTrial = urlParams.get('trial') === 'true';
      
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: '20'
      });
      
      if (isTrial) params.append('trial', 'true');
      
      // For GRE/GMAT, use section instead of subject/topic
      if (isGlobalExam) {
        if (selectedSection) params.append('subject', selectedSection); // Backend uses 'subject' field
      } else {
        if (selectedSubject) params.append('subject', selectedSubject);
        if (selectedTopic) params.append('topic', selectedTopic);
        if (selectedDifficulty) params.append('difficulty', selectedDifficulty);
      }
      
      if (searchQuery) params.append('search', searchQuery);

      const response = await axios.get(
        `${API}/exam-bank/${examId}?${params.toString()}`,
        { headers: { 'Authorization': `Bearer ${token}` } }
      );

      if (response.data.success) {
        setQuestions(response.data.questions);
        setTotalPages(response.data.pagination.total_pages);
        setTotal(response.data.pagination.total);
        
        // For GRE/GMAT, subjects are actually sections
        if (isGlobalExam) {
          setSections(response.data.filters.subjects || []);
        } else {
          setSubjects(response.data.filters.subjects || []);
          setTopics(response.data.filters.topics || []);
        }
        
        setIsTrialMode(response.data.is_trial || false);
        setHasFullAccess(response.data.has_full_access || false);
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      
      // Check if authentication is required (401)
      if (error.response?.status === 401) {
        setLoading(false);
        openAuth('register');
        // Show message that auth is required
        toast.info('Please sign in to access this exam');
        return;
      }
      
      // Check if it's an access denied error (403)
      if (error.response?.status === 403) {
        setNeedsSubscription(true);
        setLoading(false);
        return;
      }
      
      toast.error('Failed to load questions');
    } finally {
      setLoading(false);
    }
  };

  const saveAnswer = (questionId, answer) => {
    const updated = { ...userAnswers, [questionId]: answer };
    setUserAnswers(updated);
    localStorage.setItem(`exam_${examId}_answers`, JSON.stringify(updated));
  };

  const toggleBookmark = (questionId) => {
    const updated = new Set(bookmarks);
    if (updated.has(questionId)) {
      updated.delete(questionId);
    } else {
      updated.add(questionId);
    }
    setBookmarks(updated);
    localStorage.setItem(`exam_${examId}_bookmarks`, JSON.stringify([...updated]));
  };

  const toggleReveal = (questionId) => {
    const updated = new Set(revealed);
    if (updated.has(questionId)) {
      updated.delete(questionId);
    } else {
      updated.add(questionId);
    }
    setRevealed(updated);
    localStorage.setItem(`exam_${examId}_revealed`, JSON.stringify([...updated]));
  };
  
  const handleAskNerdAI = (question) => {
    // Format the question for AI - handle both MCQ and non-MCQ questions
    let formattedQuery = `I need help understanding this exam question:

Question: ${question.question_text || question.question || 'Not provided'}
`;

    // Add options only if they exist (using option_a, option_b format)
    if (question.option_a || question.option_b || question.option_c || question.option_d || question.option_e) {
      formattedQuery += `
Options:
${question.option_a ? `A) ${question.option_a}` : ''}
${question.option_b ? `B) ${question.option_b}` : ''}
${question.option_c ? `C) ${question.option_c}` : ''}
${question.option_d ? `D) ${question.option_d}` : ''}
${question.option_e ? `E) ${question.option_e}` : ''}
`;
      
      if (question.correct_answer) {
        formattedQuery += `
Correct Answer: ${question.correct_answer}`;
      }
    }
    
    // Add solution if available
    if (question.solution) {
      formattedQuery += `

Solution: ${question.solution}`;
    }
    
    // Add explanation if available
    if (question.explanation) {
      formattedQuery += `

Explanation: ${question.explanation}`;
    }

    formattedQuery += `

Please help me understand this concept better and explain the solution step by step.`;

    // Set query and increment key to force popup re-render
    setAIChatQuery(formattedQuery);
    setAIChatKey(prev => prev + 1); // Force new popup instance
    setShowAIChat(true);
  };

  const handleOptionClick = (questionId, option, correctAnswer) => {
    const currentAnswer = userAnswers[questionId];
    
    if (!currentAnswer) {
      // First selection
      saveAnswer(questionId, { selected: option, isCorrect: option === correctAnswer });
    }
  };

  // Calculate stats
  const answered = Object.keys(userAnswers).length;
  const correct = Object.values(userAnswers).filter(a => a.isCorrect).length;
  const accuracy = answered > 0 ? Math.round((correct / answered) * 100) : 0;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title={examName ? `${examName} — Mock Tests & Question Bank` : 'Exam Practice'}
        description={examName ? `Practice ${examName} with topic-wise questions, mock tests, performance analytics and detailed explanations.` : 'Practice mock tests with detailed solutions.'}
        path={`/exam-bank/${examId}`}
        ogType="exam"
      />
      {/* Header - Mobile Responsive */}
      <div className="bg-white border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
          <div className="flex items-center justify-between gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate(getBackPath())}
              className="text-xs sm:text-sm px-2 sm:px-3"
            >
              <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
              <span className="hidden xs:inline">Back</span>
            </Button>

            <h1 className="text-sm sm:text-lg md:text-xl font-bold text-gray-900 truncate max-w-[150px] sm:max-w-none text-center">
              {examId.replace(/-/g, ' ').toUpperCase()}
            </h1>

            <Button
              variant="outline"
              size="sm"
              className="lg:hidden border-green-600 text-green-600 hover:bg-green-50 text-xs sm:text-sm px-2 sm:px-3"
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
              <span className="hidden sm:inline">{showFilters ? 'Hide' : 'Filters'}</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto p-2 sm:p-4">
        {/* Trial Mode Banner - Mobile Responsive */}
        {isTrialMode && (
          <Card className="mb-3 sm:mb-4 bg-gradient-to-r from-pink-500 to-purple-600 border-0 text-white">
            <CardContent className="py-3 sm:py-4 px-3 sm:px-4">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 sm:gap-4">
                <div className="flex items-center gap-2 sm:gap-3">
                  <Sparkles className="h-5 w-5 sm:h-6 sm:w-6 flex-shrink-0" />
                  <div>
                    <p className="font-bold text-sm sm:text-lg">Free Trial Mode - 10 Questions</p>
                    <p className="text-xs sm:text-sm opacity-90">Subscribe for full access to {total} questions!</p>
                  </div>
                </div>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-white text-purple-600 hover:bg-gray-100 font-semibold text-xs sm:text-sm w-full sm:w-auto"
                >
                  Get Full Access
                </Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-3 sm:gap-4">
          {/* Left Panel - Filters (Hidden on mobile by default) */}
          <div className={`lg:col-span-3 ${showFilters ? 'block' : 'hidden lg:block'}`}>
            <Card className="lg:sticky lg:top-20">
              <CardContent className="p-3 sm:p-4">
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="w-full flex items-center justify-between mb-4 p-2 hover:bg-gray-50 rounded-lg transition-colors"
                >
                  <h3 className="font-semibold flex items-center">
                    <Filter className="h-5 w-5 mr-2 text-green-600" />
                    Filters
                  </h3>
                  <span className="text-sm text-gray-500">
                    {showFilters ? 'Collapse' : 'Expand'}
                  </span>
                </button>

                <div className={`space-y-4 ${showFilters ? 'block' : 'hidden'}`}>

                {/* Search */}
                <div className="mb-4">
                  <label className="text-sm text-gray-600 mb-1 block">Search</label>
                  <div className="relative">
                    <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                    <Input
                      placeholder="Keywords..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-8"
                    />
                  </div>
                </div>

                {/* For GRE/GMAT - Show only Section filter */}
                {isGlobalExam ? (
                  <>
                    {/* Section Filter for GRE/GMAT */}
                    {sections.length > 0 && (
                      <div className="mb-4">
                        <label className="text-sm text-gray-600 mb-1 block">Section</label>
                        <select
                          value={selectedSection}
                          onChange={(e) => setSelectedSection(e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="">All Sections</option>
                          {sections.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    {/* Standard filters for other exams */}
                    {/* Subject */}
                    {subjects.length > 0 && (
                      <div className="mb-4">
                        <label className="text-sm text-gray-600 mb-1 block">Subject</label>
                        <select
                          value={selectedSubject}
                          onChange={(e) => setSelectedSubject(e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="">All Subjects</option>
                          {subjects.map(s => (
                            <option key={s} value={s}>{s}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Topic */}
                    {topics.length > 0 && (
                      <div className="mb-4">
                        <label className="text-sm text-gray-600 mb-1 block">Topic</label>
                        <select
                          value={selectedTopic}
                          onChange={(e) => setSelectedTopic(e.target.value)}
                          className="w-full px-3 py-2 border rounded-md"
                        >
                          <option value="">All Topics</option>
                          {topics.map(t => (
                            <option key={t} value={t}>{t}</option>
                          ))}
                        </select>
                      </div>
                    )}

                    {/* Difficulty */}
                    <div className="mb-4">
                      <label className="text-sm text-gray-600 mb-1 block">Difficulty</label>
                      <select
                        value={selectedDifficulty}
                        onChange={(e) => setSelectedDifficulty(e.target.value)}
                        className="w-full px-3 py-2 border rounded-md"
                      >
                        <option value="">All Levels</option>
                        <option value="Easy">Easy</option>
                        <option value="Medium">Medium</option>
                        <option value="Hard">Hard</option>
                      </select>
                    </div>
                  </>
                )}

                {/* Clear Filters */}
                <Button
                  variant="outline"
                  size="sm"
                  className="w-full"
                  onClick={() => {
                    if (isGlobalExam) {
                      setSelectedSection('');
                    } else {
                      setSelectedSubject('');
                      setSelectedTopic('');
                      setSelectedDifficulty('');
                    }
                    setSearchQuery('');
                  }}
                >
                  Clear Filters
                </Button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Center Panel - Questions */}
          <div className="lg:col-span-6">
            {/* Pagination Top - Mobile Responsive */}
            <div className="mb-3 sm:mb-4 flex flex-col sm:flex-row items-center justify-between gap-2 bg-white p-3 sm:p-4 rounded-lg">
              <div className="text-xs sm:text-sm text-gray-600 order-2 sm:order-1">
                Showing {((page - 1) * 20) + 1}-{Math.min(page * 20, total)} of {total}
              </div>
              
              <div className="flex gap-1 sm:gap-2 order-1 sm:order-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                >
                  <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
                
                <span className="px-2 sm:px-3 py-1 text-xs sm:text-sm flex items-center">
                  {page} / {totalPages}
                </span>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-8 w-8 sm:h-9 sm:w-9 p-0"
                >
                  <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4" />
                </Button>
              </div>
            </div>

            {/* Questions */}
            {loading ? (
              <Card className="p-8 text-center">
                <RefreshCw className="h-8 w-8 animate-spin text-blue-600 mx-auto mb-2" />
                <p className="text-gray-600">Loading questions...</p>
              </Card>
            ) : needsSubscription ? (
              <Card className="p-8 text-center border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-pink-50">
                <Lock className="h-16 w-16 text-purple-600 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-3">Subscription Required</h3>
                <p className="text-gray-600 mb-6">
                  Subscribe to access this exam bank and practice unlimited questions!
                </p>
                <Button
                  onClick={() => navigate('/pricing')}
                  className="bg-gradient-to-r from-purple-600 to-pink-600 hover:opacity-90 text-white font-semibold px-8 py-6 text-lg"
                >
                  View Plans & Subscribe
                </Button>
              </Card>
            ) : questions.length === 0 ? (
              <Card className="p-6 sm:p-8 text-center">
                <BookOpen className="h-10 w-10 sm:h-12 sm:w-12 text-gray-400 mx-auto mb-3 sm:mb-4" />
                <p className="text-sm sm:text-base text-gray-600">No questions found</p>
              </Card>
            ) : (
              <div className="space-y-3 sm:space-y-4">
                {questions.map((q, idx) => {
                  const answer = userAnswers[q.id];
                  const isRevealed = revealed.has(q.id);
                  const isBookmarked = bookmarks.has(q.id);

                  return (
                    <Card key={q.id} className="relative">
                      <CardContent className="p-3 sm:p-4 md:p-6">
                        {/* Bookmark */}
                        <button
                          onClick={() => toggleBookmark(q.id)}
                          className="absolute top-3 right-3 sm:top-4 sm:right-4"
                        >
                          <Star
                            className={`h-4 w-4 sm:h-5 sm:w-5 ${isBookmarked ? 'fill-yellow-400 text-yellow-400' : 'text-gray-400'}`}
                          />
                        </button>

                        {/* Question Header - Mobile Optimized */}
                        <div className="flex items-start gap-2 sm:gap-3 mb-3 sm:mb-4 pr-6">
                          <div className="flex-shrink-0 w-6 h-6 sm:w-8 sm:h-8 bg-blue-100 rounded-full flex items-center justify-center text-blue-600 font-semibold text-xs sm:text-sm">
                            {((page - 1) * 20) + idx + 1}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex flex-wrap items-center gap-1 sm:gap-2 mb-2">
                              <span className="text-[10px] sm:text-xs text-gray-500">{q.question_id}</span>
                              
                              {/* For GRE/GMAT: Show Section instead of Difficulty */}
                              {isGlobalExam ? (
                                q.subject && (
                                  <span className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                                    {q.subject}
                                  </span>
                                )
                              ) : (
                                <>
                                  {/* For other exams: Show Difficulty and Subject */}
                                  {q.difficulty_level && (
                                    <span className={`text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 rounded ${
                                      q.difficulty_level === 'Easy' ? 'bg-green-100 text-green-700' :
                                      q.difficulty_level === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                                      'bg-red-100 text-red-700'
                                    }`}>
                                      {q.difficulty_level}
                                    </span>
                                  )}
                                  {q.subject && (
                                    <span className="text-[10px] sm:text-xs text-gray-600 truncate max-w-[100px] sm:max-w-none">{q.subject}</span>
                                  )}
                                </>
                              )}
                            </div>
                            <p className="text-sm sm:text-base text-gray-900 leading-relaxed">{q.question_text}</p>
                          </div>
                        </div>

                        {/* Options - Mobile Optimized */}
                        {(q.option_a || q.option_b || q.option_c || q.option_d) ? (
                          <div className="space-y-2 sm:space-y-3 mb-3 sm:mb-4">
                            {['option_a', 'option_b', 'option_c', 'option_d', 'option_e'].map((opt, i) => {
                              if (!q[opt]) return null;
                              
                              const letter = String.fromCharCode(65 + i);
                              const isSelected = answer?.selected === letter;
                              const isCorrect = q.correct_answer === letter;
                              const showCorrect = answer && isCorrect;
                              const showWrong = answer && isSelected && !answer.isCorrect;

                              return (
                                <button
                                  key={opt}
                                  onClick={() => handleOptionClick(q.id, letter, q.correct_answer)}
                                  disabled={!!answer}
                                  className={`w-full text-left rounded-lg border-2 transition-all ${
                                    showCorrect ? 'bg-green-50 border-green-500' :
                                    showWrong ? 'bg-red-50 border-red-500' :
                                    isSelected ? 'bg-blue-50 border-blue-500' :
                                    'border-gray-200 hover:border-blue-300 hover:bg-blue-50'
                                  } ${answer ? 'cursor-not-allowed' : 'cursor-pointer'}`}
                                  style={{ minHeight: 'auto', height: 'auto' }}
                                >
                                  <div className="flex w-full min-h-[40px] sm:min-h-[44px]">
                                    {/* Letter Badge - Fixed width column */}
                                    <div className={`flex-shrink-0 w-8 sm:w-10 flex items-center justify-center ${
                                      showCorrect ? 'bg-green-500' :
                                      showWrong ? 'bg-red-500' :
                                      isSelected ? 'bg-blue-500' :
                                      'bg-gray-100'
                                    }`}>
                                      <span className={`text-xs sm:text-sm font-bold ${
                                        showCorrect || showWrong || isSelected ? 'text-white' : 'text-gray-700'
                                      }`}>
                                        {letter}
                                      </span>
                                    </div>
                                    
                                    {/* Option Text - Flexible width, wraps naturally */}
                                    <div className="flex-1 py-2 sm:py-2.5 px-2.5 sm:px-3 flex items-center justify-between gap-2">
                                      <p className="text-xs sm:text-sm text-gray-900 leading-normal whitespace-normal break-words w-full">{q[opt]}</p>
                                      {showCorrect && <Check className="h-4 w-4 sm:h-5 sm:w-5 text-green-600 flex-shrink-0" />}
                                      {showWrong && <X className="h-4 w-4 sm:h-5 sm:w-5 text-red-600 flex-shrink-0" />}
                                    </div>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        ) : (
                          /* Direct Answer Input - For non-MCQ questions */
                          <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Your Answer:
                            </label>
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                placeholder="Type your answer here..."
                                value={answer?.userInput || ''}
                                onChange={(e) => {
                                  if (!answer?.isChecked) {
                                    setUserAnswers(prev => ({
                                      ...prev,
                                      [q.id]: { 
                                        ...prev[q.id],
                                        userInput: e.target.value 
                                      }
                                    }));
                                  }
                                }}
                                disabled={answer?.isChecked}
                                className="flex-1"
                              />
                              {!answer?.isChecked && (
                                <Button
                                  onClick={() => {
                                    const userInput = userAnswers[q.id]?.userInput || '';
                                    if (!userInput.trim()) {
                                      toast.error('Please enter your answer');
                                      return;
                                    }
                                    
                                    // Compare with solution (case-insensitive, trim whitespace)
                                    const normalizedUserAnswer = userInput.trim().toLowerCase();
                                    const normalizedCorrectAnswer = (q.solution || q.correct_answer || '').trim().toLowerCase();
                                    const isCorrect = normalizedUserAnswer === normalizedCorrectAnswer;
                                    
                                    setUserAnswers(prev => ({
                                      ...prev,
                                      [q.id]: {
                                        ...prev[q.id],
                                        isChecked: true,
                                        isCorrect: isCorrect
                                      }
                                    }));
                                    
                                    // Save to localStorage
                                    const updatedAnswers = {
                                      ...userAnswers,
                                      [q.id]: {
                                        userInput: userInput,
                                        isChecked: true,
                                        isCorrect: isCorrect
                                      }
                                    };
                                    localStorage.setItem(`exam_${examId}_answers`, JSON.stringify(updatedAnswers));
                                  }}
                                  className="bg-blue-700 hover:bg-blue-700"
                                >
                                  Check Answer
                                </Button>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Feedback - Mobile Optimized */}
                        {answer && (answer.isCorrect !== undefined) && (
                          <div className={`p-2 sm:p-3 rounded-lg mb-2 sm:mb-3 ${
                            answer.isCorrect ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
                          }`}>
                            <div className="flex items-center gap-1.5 sm:gap-2">
                              {answer.isCorrect ? (
                                <>
                                  <Check className="h-4 w-4 sm:h-5 sm:w-5" />
                                  <span className="font-semibold text-xs sm:text-sm">✅ Correct!</span>
                                </>
                              ) : (
                                <>
                                  <X className="h-4 w-4 sm:h-5 sm:w-5" />
                                  <span className="font-semibold text-xs sm:text-sm">
                                    ❌ Wrong! Answer: {q.solution || q.correct_answer}
                                  </span>
                                </>
                              )}
                            </div>
                          </div>
                        )}

                        {/* Reveal Answer Button - Mobile Optimized */}
                        {answer && (answer.selected || answer.isChecked) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => toggleReveal(q.id)}
                            className="w-full text-xs sm:text-sm py-1.5 sm:py-2"
                          >
                            <Eye className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                            {isRevealed ? 'Hide' : 'Show'} Explanation
                          </Button>
                        )}

                        {/* Explanation - Mobile Optimized */}
                        {isRevealed && q.explanation && (
                          <div className="mt-3 sm:mt-4 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                            <h4 className="font-semibold text-blue-900 mb-1.5 sm:mb-2 text-sm sm:text-base">Explanation:</h4>
                            <p className="text-blue-800 text-xs sm:text-sm leading-relaxed">{q.explanation}</p>
                            
                            {/* Ask Nerd-AI Button - Only for users with AI Buddy access or trial mode */}
                            {(hasAIBuddyAccess || isTrialMode) && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={async () => {
                                  setLoadingAskNerd(true);
                                  try {
                                    await handleAskNerdAI(q);
                                  } finally {
                                    setLoadingAskNerd(false);
                                  }
                                }}
                                disabled={loadingAskNerd}
                                className="mt-2 sm:mt-3 w-full bg-gradient-to-r from-purple-50 to-pink-50 border-purple-300 hover:border-purple-400 text-purple-700 hover:text-purple-900 text-xs sm:text-sm"
                              >
                                {loadingAskNerd ? (
                                  <>
                                    <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2 animate-spin" />
                                    Processing...
                                  </>
                                ) : (
                                  <>
                                    <Brain className="h-3 w-3 sm:h-4 sm:w-4 mr-1.5 sm:mr-2" />
                                    Ask Nerd-AI
                                  </>
                                )}
                              </Button>
                            )}
                          </div>
                        )}
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}

            {/* Pagination Bottom - Mobile Optimized */}
            {!loading && questions.length > 0 && (
              <div className="mt-3 sm:mt-4 flex justify-center gap-1.5 sm:gap-2">
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="text-xs sm:text-sm px-2 sm:px-3"
                >
                  <ChevronLeft className="h-3 w-3 sm:h-4 sm:w-4 mr-0.5 sm:mr-1" />
                  <span className="hidden xs:inline">Previous</span>
                </Button>
                
                <Button
                  variant="outline"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="text-xs sm:text-sm px-2 sm:px-3"
                >
                  <span className="hidden xs:inline">Next</span>
                  <ChevronRight className="h-3 w-3 sm:h-4 sm:w-4 ml-0.5 sm:ml-1" />
                </Button>
              </div>
            )}
            
            {/* Trial End Banner - Mobile Optimized */}
            {isTrialMode && page >= totalPages && (
              <Card className="mt-4 sm:mt-6 bg-gradient-to-r from-pink-500 to-purple-600 border-0 text-white">
                <CardContent className="py-4 sm:py-6 px-3 sm:px-4">
                  <div className="text-center">
                    <Lock className="h-10 w-10 sm:h-12 sm:w-12 mx-auto mb-2 sm:mb-3" />
                    <h3 className="text-lg sm:text-2xl font-bold mb-1 sm:mb-2">Trial Complete!</h3>
                    <p className="text-sm sm:text-lg opacity-90 mb-3 sm:mb-4">
                      Get full access to {total} questions!
                    </p>
                    <Button
                      onClick={() => navigate('/pricing')}
                      className="bg-white text-purple-600 hover:bg-gray-100 font-semibold px-4 sm:px-8 py-2 sm:py-4 text-sm sm:text-lg"
                    >
                      Get Full Access
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}
          </div>

          {/* Right Panel - Stats (Hidden on mobile, shown at top on tablet) */}
          <div className="lg:col-span-3 hidden lg:block">
            <Card className="sticky top-20">
              <CardContent className="p-3 sm:p-4">
                <h3 className="font-semibold mb-3 sm:mb-4 text-sm sm:text-base">Your Progress</h3>

                <div className="space-y-3 sm:space-y-4">
                  <div>
                    <div className="flex justify-between text-xs sm:text-sm mb-1">
                      <span className="text-gray-600">Answered</span>
                      <span className="font-semibold">{answered} / {total}</span>
                    </div>
                    <div className="w-full bg-gray-200 rounded-full h-1.5 sm:h-2">
                      <div
                        className="bg-blue-700 h-1.5 sm:h-2 rounded-full transition-all"
                        style={{ width: `${total > 0 ? (answered / total) * 100 : 0}%` }}
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-2 sm:gap-3">
                    <div className="bg-green-50 p-2 sm:p-3 rounded-lg">
                      <p className="text-lg sm:text-2xl font-bold text-green-700">{correct}</p>
                      <p className="text-[10px] sm:text-xs text-green-600">Correct</p>
                    </div>
                    <div className="bg-red-50 p-2 sm:p-3 rounded-lg">
                      <p className="text-lg sm:text-2xl font-bold text-red-700">{answered - correct}</p>
                      <p className="text-[10px] sm:text-xs text-red-600">Wrong</p>
                    </div>
                  </div>

                  <div className="bg-purple-50 p-2 sm:p-3 rounded-lg">
                    <p className="text-xl sm:text-3xl font-bold text-purple-700">{accuracy}%</p>
                    <p className="text-[10px] sm:text-xs text-purple-600">Accuracy</p>
                  </div>

                  <div className="bg-yellow-50 p-2 sm:p-3 rounded-lg">
                    <div className="flex items-center justify-between">
                      <Star className="h-4 w-4 sm:h-5 sm:w-5 fill-yellow-400 text-yellow-400" />
                      <p className="text-base sm:text-xl font-bold text-yellow-700">{bookmarks.size}</p>
                    </div>
                    <p className="text-[10px] sm:text-xs text-yellow-600 mt-1">Bookmarked</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
        
        {/* Mobile Stats Bar - Fixed at bottom */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-2 flex justify-around lg:hidden z-40">
          <div className="text-center">
            <p className="text-xs text-gray-500">Answered</p>
            <p className="text-sm font-bold text-blue-600">{answered}/{total}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Correct</p>
            <p className="text-sm font-bold text-green-600">{correct}</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Accuracy</p>
            <p className="text-sm font-bold text-purple-600">{accuracy}%</p>
          </div>
          <div className="text-center">
            <p className="text-xs text-gray-500">Saved</p>
            <p className="text-sm font-bold text-yellow-600">{bookmarks.size}</p>
          </div>
        </div>
        
        {/* Bottom spacing for mobile stats bar */}
        <div className="pb-20 lg:pb-24"></div>
      </div>
      
      {/* Nerd-AI Chat Popup */}
      <NerdAIChatPopup
        key={aiChatKey}
        isOpen={showAIChat}
        onClose={() => setShowAIChat(false)}
        initialQuery={aiChatQuery}
        examName={examId?.toUpperCase()}
      />
      
    </div>
  );
};

export default ExamBankPractice;
